/**
 * @fileoverview User Management notices hook.
 * @description The confirmations (a flash that clears itself) and the one action error the User
 * Management page shows, with a message for each thing that can happen: a user added, updated,
 * deactivated or reactivated (an invited-again user is said to be invited), an invitation
 * re-sent or revoked, or not found.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFlashMessage } from '@/shared/hooks/useFlashMessage'

import type { ReactivatedUser, UserDetail } from '@/features/users/types/users.types'

/**
 * @description Keeps the page's flash and action error, and the messages it shows.
 * @returns {object} The flash, the action error, and a handler for each thing that can happen.
 */
export function useUserManagementNotices() {
  const { t } = useTranslation()
  const flash = useFlashMessage()
  const [actionError, setActionError] = useState('')

  /**
   * @description Confirms a user was added.
   * @param {unknown} _response - The invitation the backend created (unused).
   * @param {string} name - The new user's name.
   * @param {string} roleLabel - The role they were invited as.
   */
  const onUserCreated = (_response: unknown, name: string, roleLabel: string) => {
    flash.show(t('sales.userManagement.flash.created', { name, role: roleLabel }))
  }

  /**
   * @description Confirms a user was updated.
   * @param {UserDetail} updated - The user as they are now.
   */
  const onUserUpdated = (updated: UserDetail) => {
    flash.show(t('sales.userManagement.flash.updated', { name: updated.displayName }))
  }

  /**
   * @description Confirms a user was deactivated.
   * @param {UserDetail} deactivated - The deactivated user.
   */
  const onUserDeactivated = (deactivated: UserDetail) => {
    flash.show(t('sales.userManagement.flash.deactivated', { name: deactivated.displayName }))
  }

  /**
   * @description Reports a reactivated user: a restored user is welcomed back, a user who never
   * accepted their invitation is invited again.
   * @param {ReactivatedUser} result - The reactivation result.
   */
  const onReactivated = ({ user, reactivationMode }: ReactivatedUser) => {
    flash.show(
      t(
        reactivationMode === 'INVITATION_RESTART'
          ? 'sales.userManagement.flash.reactivatedInvited'
          : 'sales.userManagement.flash.reactivated',
        { name: user.displayName },
      ),
    )
  }

  /**
   * @description Confirms an invitation was re-sent.
   * @param {string} name - The invited user's name.
   */
  const onInvitationResent = (name: string) => {
    flash.show(t('sales.userManagement.flash.invitationResent', { name }))
  }

  /**
   * @description Confirms an invitation was revoked.
   * @param {{ email: string }} invitation - The revoked invitation.
   */
  const onInvitationRevoked = (invitation: { email: string }) => {
    flash.show(t('userInvitations.list.revoked', { email: invitation.email }))
  }

  /**
   * @description Says a user has no invitation to revoke.
   * @param {string} name - The user's name.
   */
  const onInvitationNotFound = (name: string) => {
    setActionError(t('sales.userManagement.invitationNotFound', { name }))
  }

  /**
   * @description Clears the action error, as a new action starts.
   */
  const clearActionError = () => {
    setActionError('')
  }

  return {
    flash: flash.message,
    dismissFlash: flash.dismiss,
    actionError,
    showActionError: setActionError,
    clearActionError,
    onUserCreated,
    onUserUpdated,
    onUserDeactivated,
    onReactivated,
    onInvitationResent,
    onInvitationRevoked,
    onInvitationNotFound,
  }
}
